import {
  useCallback,
  useEffect,
  useState,
  type FocusEvent as ReactFocusEvent,
} from 'react';

import { _useFocusContext } from '@dynatrace/strato-components/core';
import Borders from '@dynatrace/strato-design-tokens/borders';
import Colors from '@dynatrace/strato-design-tokens/colors';

/**
 * @public
 */
export interface UseFocusRingProps {
  /**
   * A minimal focus outline is available for smaller content elements like links. This variant is applied when set to true.
   * @defaultValue false
   */
  isMinimal?: boolean;
  /**
   * Whether or not the modality should be ignored, so that the focus ring is also present if users clicks the element (not just by tabbing to it).
   * @defaultValue false
   */
  ignoreModality?: boolean;
  /**
   * The styling color of the element.
   * @defaultValue `neutral`
   */
  color?: 'neutral' | 'primary' | 'success' | 'warning' | 'critical';

  /**
   * Apply the focus ring styling if an embedded element gains the focus
   */
  focusWithin?: boolean;

  /**
   * Whether the element is disabled. If disabled changes to true, the focus will be removed.
   */
  disabled?: boolean;
}

/**
 * @public
 */
export interface CSSFocusStylesObject {
  [key: string]: CSSFocusStylesObject | string | number | undefined;
  '&:focus-visible'?: CSSFocusStylesObject;
  '&:focus-within'?: CSSFocusStylesObject;
}

/**
 * @public
 */
export type FocusRingProps = {
  /** The focus styles that depend on modality. */
  focusStyles: CSSFocusStylesObject;
  /** Focus handlers used to manage the focus state. Must be applied to the element that receives the focus.*/
  focusProps: {
    onFocus: (e: ReactFocusEvent | FocusEvent) => void;
    onBlur: (e: ReactFocusEvent | FocusEvent) => void;
  };
  /** Whether or not the element got focus via keyboard. */
  isFocusVisible: boolean;
};

/**
 * Custom hook used to determine focus styling depending on modality.
 * Returns CSS that can be applied to the component in order to show the correct focus outline.
 * @public
 */
export function useFocusRing(props: UseFocusRingProps = {}): FocusRingProps {
  const {
    color = 'neutral',
    ignoreModality = false,
    isMinimal = false,
    focusWithin = false,
    disabled = false,
  } = props;
  const [isFocused, setIsFocused] = useState(false);
  const { modality } = _useFocusContext();

  useEffect(() => {
    if (disabled) {
      setIsFocused(false);
    }
  }, [disabled]);

  const onBlur = useCallback((e: ReactFocusEvent | FocusEvent) => {
    // trigger only when the self element is unfocused (stopPropagation)
    if (e.currentTarget === e.target) {
      setIsFocused(false);
    }
  }, []);

  const onFocus = useCallback((e: ReactFocusEvent | FocusEvent) => {
    // trigger only when the self element is focused (stopPropagation)
    if (e.currentTarget === e.target) {
      setIsFocused(true);
    }
  }, []);

  const focusProps = {
    onFocus,
    onBlur,
  };

  if (
    ((ignoreModality || modality === 'keyboard') && isFocused) ||
    (focusWithin && !disabled)
  ) {
    const CapitalizedVariant = `${color.charAt(0).toUpperCase()}${color.slice(
      1,
    )}` as Capitalize<
      'neutral' | 'primary' | 'success' | 'warning' | 'critical'
    >;

    const outerboxShadow = Colors.Border[CapitalizedVariant].Accent;
    const innerShadow = Colors.Border[CapitalizedVariant].Default;

    if (isMinimal) {
      const minimalFocusStyle: CSSFocusStylesObject = {
        outline: 'none',
        boxShadow: `inset 0 0 0 ${Borders.Width.Default} ${outerboxShadow}`,
        borderRadius: `calc(${Borders.Radius.Field.Subdued} / 2)`,
        backgroundColor: Colors.Background.Field[CapitalizedVariant].Emphasized,
        '&:focus-visible': { outline: 'none' },
      };
      return {
        focusStyles: focusWithin
          ? { '&:focus-within': minimalFocusStyle }
          : minimalFocusStyle,
        isFocusVisible: !focusWithin,
        focusProps,
      };
    }

    const formalFocusStyle: CSSFocusStylesObject = {
      outline: 'none',
      boxShadow: `inset 0 0 0 ${Borders.Width.Emphasized} ${outerboxShadow}, inset 0 0 0 calc(${Borders.Width.Emphasized} + 1px) ${innerShadow}`,
      '&:focus-visible': { outline: 'none' },
    };

    return {
      focusStyles: focusWithin
        ? { '&:focus-within': formalFocusStyle }
        : formalFocusStyle,
      isFocusVisible: !focusWithin,
      focusProps,
    };
  }

  return {
    focusStyles: { outline: 'none', '&:focus-visible': { outline: 'none' } }, // fixes the flicker effect from removing the native outline, which otherwise happens too late
    isFocusVisible: false,
    focusProps,
  };
}
